import type { WerkbonBundelRefDTO } from './verzamelbon'
import { z } from 'zod'
import type { WerkbonFacturatieDTO } from './werkbon-facturatie'
// batch-63 werkbon-facturatie (FO-474) — blad-module (geen runtime-cyclus).
import {
  controleerFacturabelKeuze,
  facturabelRedenSchema,
  type WerkbonNietFacturabelReden,
  type WerkbonNietFacturabelRedenFormulier,
} from './werkbon-facturabel-reden'
import type { QuoteStatus } from './index'
import { idParam } from './id-validators'

// ─── §5 Planmodus ─────────────────────────────────────────────────────────

export type PlanModus = 'weken' | 'datumbereik' | 'losse-dagen'

/** YYYY-MM-DD string (geen Date). */
export type DateISO = string

// ─── Enums (matching Prisma enums as string unions) ─────────────────────────

// Werkbon-status vereenvoudiging (2026-05-27) — operationele status, AUTOMATISCH
// berekend uit dayEntries + medewerker + datums (computeWerkbonStatus). De oude
// goedkeurings-statussen zijn weg; goedkeuring zit in WerkbonGoedkeuringStatus.
// Facturatie = invoiceId != null; verloning = isVerwerktInVerloning.
export type WerkbonStatus = 'NIET_GEPLAND' | 'GEPLAND' | 'AFGEROND'

// Twee-staps goedkeuringsflow (intern → klant) die start zodra de werkbon
// AFGEROND is. Los van WerkbonStatus. null = (nog) geen flow / gereset na afkeur.
export type WerkbonGoedkeuringStatus =
  | 'WACHT_OP_INTERN'
  | 'GOEDGEKEURD_INTERN'
  | 'AFGEKEURD_INTERN'
  | 'WACHT_OP_KLANT'
  | 'GOEDGEKEURD_KLANT'
  | 'AFGEKEURD_KLANT'

export type DayEntryStatus = 'GEPLAND' | 'ACTIEF' | 'GESLOTEN' | 'HEROPEND'

// PR-E (spec 17 §3) — `UREN_ONJUIST` is verwijderd; functioneel hetzelfde als
// `TIJDEN_ONJUIST`. Label van `TIJDEN_ONJUIST` is verbreed naar
// "Uren of tijden onjuist". Bestaande rijen zijn in DB-migratie
// `consolidate-day-reject-reasons` automatisch geconverteerd.

export type DayRejectReason =
  | 'TIJDEN_ONJUIST'
  | 'REIS_ONJUIST'
  | 'OMSCHRIJVING_ONTBREEKT'
  | 'MATERIAAL_ONJUIST'
  | 'VERKEERDE_WERKBON'
  | 'ANDERS'

export const DAY_REJECT_REASONS: readonly DayRejectReason[] = [
  'TIJDEN_ONJUIST',
  'REIS_ONJUIST',
  'OMSCHRIJVING_ONTBREEKT',
  'MATERIAAL_ONJUIST',
  'VERKEERDE_WERKBON',
  'ANDERS',
] as const

export const DAY_REJECT_REASON_LABELS: Record<DayRejectReason, string> = {
  TIJDEN_ONJUIST: 'Uren of tijden onjuist',
  REIS_ONJUIST: 'Reistijd wijkt af zonder goede reden',
  OMSCHRIJVING_ONTBREEKT: 'Omschrijving ontbreekt',
  MATERIAAL_ONJUIST: 'Materiaal onjuist',
  VERKEERDE_WERKBON: 'Verkeerde werkbon',
  ANDERS: 'Anders',
}

export type WerkbonType = 'ONDERHOUD' | 'SERVICE' | 'PROJECT' | 'KANTOOR'
// PR-4 (spec 02 §2.4) — DienstType krijgt CONSIGNATIE waarde voor planning-
// bord-filters en consignatie-werkbonnen.
export type DienstType =
  | 'DAGDIENST'
  | 'PLOEGEN_2'
  | 'PLOEGEN_3'
  | 'PLOEGEN_4'
  | 'PLOEGEN_5'
  | 'CONSIGNATIE'
export type PloegSoort = 'DAG' | 'VROEG' | 'LAAT' | 'NACHT'
// A3 (facturen.md) — vaste prijs is verwijderd; facturatie is altijd
// nacalculatie. De enum-waarde VASTPRIJS bestaat niet meer in het datamodel.
export type FactuurType = 'NACALCULATIE'
// B6 — vervangingsbehoefte op de werkbon ("moet deze monteur vervangen worden
// bij verlof/ziekte?"). Default volgt uit het servicepakket; planner-overrulebaar.
export type VervangingStatus = 'GEEN' | 'GEWENST' | 'VERPLICHT'

export const VERVANGING_STATUS_LABELS: Record<VervangingStatus, string> = {
  GEEN: 'Geen vervanging',
  GEWENST: 'Vervanging gewenst',
  VERPLICHT: 'Vervanging verplicht',
}

// PR-4 (spec 02 §2.2) — ConsignatieType: WEEK_NACHT/WEEKEND/FEESTDAG/CUSTOM.
export type ConsignatieType = 'WEEK_NACHT' | 'WEEKEND' | 'FEESTDAG' | 'CUSTOM'

export const CONSIGNATIE_TYPE_LABELS: Record<ConsignatieType, string> = {
  WEEK_NACHT: 'Weeknacht (hele week)',
  WEEKEND: 'Weekend',
  FEESTDAG: 'Feestdag',
  CUSTOM: 'Vrij',
}

/** Korte labels voor factuur-omschrijvingen (spec §5.3 — CONSIGNATIE_TYPE_LABELS). */
export const CONSIGNATIE_TYPE_INVOICE_LABELS: Record<ConsignatieType, string> = {
  WEEK_NACHT: 'weeknacht',
  WEEKEND: 'weekend',
  FEESTDAG: 'feestdag',
  CUSTOM: '',
}

// SERVICE komt uit fs-pro `detachering` — UI-label blijft "Service".
export const WERKBON_TYPE_LABELS: Record<WerkbonType, string> = {
  ONDERHOUD: 'Onderhoud',
  SERVICE: 'Service',
  PROJECT: 'Project',
  KANTOOR: 'Kantoor',
}

export const WERKBON_STATUS_LABELS: Record<WerkbonStatus, string> = {
  NIET_GEPLAND: 'Niet gepland',
  GEPLAND: 'Gepland',
  AFGEROND: 'Afgerond',
}

export const WERKBON_STATUS_BADGE: Record<
  WerkbonStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  NIET_GEPLAND: 'warning', // oranje — actie nodig (inplannen)
  GEPLAND: 'info', // blauw — loopt
  AFGEROND: 'success', // groen — klaar
}

export const WERKBON_GOEDKEURING_LABELS: Record<WerkbonGoedkeuringStatus, string> = {
  WACHT_OP_INTERN: 'Wacht op intern',
  GOEDGEKEURD_INTERN: 'Intern goedgekeurd',
  AFGEKEURD_INTERN: 'Intern afgekeurd',
  WACHT_OP_KLANT: 'Wacht op klant',
  GOEDGEKEURD_KLANT: 'Klant akkoord',
  AFGEKEURD_KLANT: 'Klant afgekeurd',
}

export const WERKBON_GOEDKEURING_BADGE: Record<
  WerkbonGoedkeuringStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  WACHT_OP_INTERN: 'warning',
  GOEDGEKEURD_INTERN: 'info',
  AFGEKEURD_INTERN: 'danger',
  WACHT_OP_KLANT: 'warning',
  GOEDGEKEURD_KLANT: 'success',
  AFGEKEURD_KLANT: 'danger',
}

export const DAY_ENTRY_STATUS_LABELS: Record<DayEntryStatus, string> = {
  GEPLAND: 'Gepland',
  ACTIEF: 'Actief',
  GESLOTEN: 'Gesloten',
  HEROPEND: 'Heropend',
}

// Gestructureerde afwezigheidsreden voor een werkbon-dag zonder gewerkte uren
// (werkbon-reisuren-afwezig-bv-nummer). Verplicht bij gereedmelden zodra de dag
// als afwezig is gemarkeerd; mirror van de Prisma-enum DayEntryAfwezigReden.
export const DAY_ENTRY_AFWEZIG_REDENEN = ['ZIEKTE', 'VRIJ', 'OVERIG'] as const
export type DayEntryAfwezigReden = (typeof DAY_ENTRY_AFWEZIG_REDENEN)[number]

export const DAY_ENTRY_AFWEZIG_REDEN_LABELS: Record<DayEntryAfwezigReden, string> = {
  ZIEKTE: 'Ziekte',
  VRIJ: 'Vrij',
  OVERIG: 'Overig',
}

export const FACTUUR_TYPE_LABELS: Record<FactuurType, string> = {
  NACALCULATIE: 'Nacalculatie',
}

export const DIENST_TYPE_LABELS: Record<DienstType, string> = {
  DAGDIENST: 'Dagdienst',
  PLOEGEN_2: '2-ploegendienst',
  PLOEGEN_3: '3-ploegendienst',
  PLOEGEN_4: '4-ploegendienst',
  PLOEGEN_5: '5-ploegendienst',
  CONSIGNATIE: 'Consignatie',
}

// ─── Ronde 4 W6 — WerkbonPriority (gemigreerd vanuit OrderPriority) ─────────
//
// De prioriteit-as zat tot Ronde 4 op het Order-model. Order is verwijderd
// (Werkbon koppelt direct aan Client). Onderstaande constanten + labels +
// badges zijn 1:1 overgenomen zodat fp-pro UI/badges blijven werken.

export const WERKBON_PRIORITY = ['LAAG', 'NORMAAL', 'HOOG', 'KRITIEK'] as const
export type WerkbonPriority = (typeof WERKBON_PRIORITY)[number]

export const WERKBON_PRIORITY_LABELS: Record<WerkbonPriority, string> = {
  LAAG: 'Laag',
  NORMAAL: 'Normaal',
  HOOG: 'Hoog',
  KRITIEK: 'Kritiek',
}

export const WERKBON_PRIORITY_BADGE: Record<
  WerkbonPriority,
  'secondary' | 'info' | 'warning' | 'danger'
> = {
  LAAG: 'secondary',
  NORMAAL: 'info',
  HOOG: 'warning',
  KRITIEK: 'danger',
}

export type ShiftType = 'DAG' | 'PLOEGEN_2' | 'PLOEGEN_3' | 'PLOEGEN_4' | 'PLOEGEN_5'
export type PackageType = 'BASIS' | 'SERVICE' | 'ONTZORG' | 'FLEX'
export type InvoiceStatus =
  | 'CONCEPT'
  | 'GEREED'
  | 'VERSTUURD_KLANT'
  | 'AKKOORD_KLANT'
  | 'AFGEKEURD_KLANT'
  | 'DEFINITIEF'
  | 'VERZONDEN'
  | 'BETAALD'
  // Drie-routes facturatie (stap 1) — Finqle-flow statussen (spiegelt Prisma InvoiceStatus).
  | 'VERZONDEN_NAAR_FINQLE'
  | 'IN_BEHANDELING_FINQLE'
  | 'GEFACTUREERD'

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  CONCEPT: 'Concept',
  GEREED: 'Gereed',
  VERSTUURD_KLANT: 'Verstuurd naar klant',
  AKKOORD_KLANT: 'Akkoord klant',
  AFGEKEURD_KLANT: 'Afgekeurd door klant',
  DEFINITIEF: 'Definitief',
  VERZONDEN: 'Verzonden naar admin',
  BETAALD: 'Betaald',
  VERZONDEN_NAAR_FINQLE: 'Verzonden naar Finqle',
  IN_BEHANDELING_FINQLE: 'In behandeling bij Finqle',
  GEFACTUREERD: 'Gefactureerd',
}

export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  CONCEPT: 'bg-gray-100 text-gray-700',
  GEREED: 'bg-amber-100 text-amber-700',
  VERSTUURD_KLANT: 'bg-blue-100 text-blue-700',
  AKKOORD_KLANT: 'bg-emerald-100 text-emerald-700',
  AFGEKEURD_KLANT: 'bg-red-100 text-red-700',
  DEFINITIEF: 'bg-purple-100 text-purple-700',
  VERZONDEN: 'bg-indigo-100 text-indigo-700',
  BETAALD: 'bg-green-100 text-green-700',
  VERZONDEN_NAAR_FINQLE: 'bg-cyan-100 text-cyan-700',
  IN_BEHANDELING_FINQLE: 'bg-sky-100 text-sky-700',
  GEFACTUREERD: 'bg-teal-100 text-teal-700',
}

export type InvoiceTransitionAction =
  | 'maak-gereed'
  | 'terug-naar-concept'
  | 'verstuur-naar-klant'
  | 'klant-akkoord'
  | 'klant-afgekeurd'
  | 'heropen'
  | 'maak-definitief'
  | 'markeer-verzonden'
  | 'markeer-betaald'
  // Batch-3 D — handmatig betaald terugdraaien (BETAALD -> vorige uitgegeven status).
  | 'markeer-onbetaald'
  // Batch-10 fa.1 — handmatig op GEFACTUREERD zetten (alleen de eigenaar/Kasper).
  // Wordt in PATCH /invoices/:id/status als aparte branch afgehandeld en staat
  // bewust NIET in INVOICE_TRANSITIONS_BY_STATUS, anders zou elke canEdit-user
  // de actie aangeboden krijgen.
  | 'markeer-gefactureerd'

// PR-4 (spec 02 §2.5 + master-prompt §1.4): drie nieuwe factuurregel-
// categorieën. CONSIGNATIE_STANDBY = vast standby-bedrag; CONSIGNATIE_OPROEP =
// oproep-uren tegen oproep-uurtarief; VAST_BEDRAG = generieke vast-bedrag-
// regel (uitbreidbaar naar bv. abonnementen).
export type InvoiceLineCategory =
  | 'ARBEID'
  | 'OVERUREN'
  | 'REISKOSTEN'
  | 'REISTIJD'
  | 'MATERIAAL'
  | 'PLOEGENTOESLAG'
  | 'CONSIGNATIE_STANDBY'
  | 'CONSIGNATIE_OPROEP'
  | 'VAST_BEDRAG'
  // PR-K (spec 09 §2.4) — handmatige + credit-categorieën
  | 'ADVIES'
  | 'TOESLAG'
  | 'KORTING'
  | 'OVERIG'
  | 'CREDIT'
  // batch-68 controle (FO-343 §4.3/§4.4) - aanvulling tot het dagminimum en
  // meerwerk boven de inbegrepen uren van een vaste prijs.
  | 'MINIMUMAFNAME'
  | 'MEERWERK'

// PR-K (spec 09 §2.2) — bron van een factuur
// FO-406 (batch-51) — FINQLE_API: factuur die Finqle via de merchant-API heeft
// opgemaakt en die STREEP spiegelt (sync/webhook), geen eigen opmaak.
export type InvoiceOrigin =
  | 'WERKBON'
  | 'OFFERTE'
  | 'HANDMATIG'
  | 'CREDIT'
  | 'IMPORT'
  | 'FINQLE_API'

export const INVOICE_ORIGIN_LABELS: Record<InvoiceOrigin, string> = {
  WERKBON: 'Werkbon',
  OFFERTE: 'Offerte',
  HANDMATIG: 'Handmatig',
  CREDIT: 'Credit',
  IMPORT: 'Import',
  FINQLE_API: 'Finqle API',
}

// PR-K (spec 09 §6.2) — type creditnota
export type CreditType = 'VOLLEDIG' | 'REISTIJD' | 'HANDMATIG'

export const CREDIT_TYPE_LABELS: Record<CreditType, string> = {
  VOLLEDIG: '100% crediteren',
  REISTIJD: 'Alleen reistijd / reiskosten',
  HANDMATIG: 'Handmatige selectie',
}

// ─── DTOs ──────────────────────────────────────────────────────────────────

export interface WerkbonDayEntryDTO {
  id: string
  position: number
  date: string
  plannedStart: string
  plannedEnd: string
  actualStart: string | null
  actualEnd: string | null
  pauzeMinutes: number
  /**
   * FO-473 - de tweeassige pauze (verloning + facturatie, met herkomst per as).
   * `hours` = de BETAALDE uren; `pauze.facturabeleUren` = de facturabele uren.
   * Optioneel voor oudere clients.
   */
  pauze?: import('./pauze-tweeassig').DagregelPauzeDTO
  hours: number
  overuren: number
  reisKm: number
  reisMinuten: number
  reisVergoeding: number
  werkSegments: Array<{ start: string; end: string; pauzeMinutes?: number }>
  reisSegments: Array<{ start: string; end: string }>
  status: DayEntryStatus
  /** PR-4 (spec 02 §2.3): bij consignatie-werkbon = daadwerkelijke oproep. */
  isOproep: boolean
  // Werkbon-status vereenvoudiging (2026-05-27) — `ingediendOp` is verwijderd
  // van DayEntry (geen handmatige indiening meer; status volstaat).
  goedgekeurdById: string | null
  goedgekeurdOp: string | null
  afgekeurdRedenen: DayRejectReason[]
  afgekeurdToelichting: string | null
  afgekeurdOp: string | null
  afgekeurdById: string | null
  afgekeurdBy: { id: string; name: string | null } | null
  description: string | null
  employee: {
    id: string
    firstName: string
    lastName: string
    employeeNumber: string | null
  } | null
}

export interface QueueDayEntryDTO extends WerkbonDayEntryDTO {
  werkbon: {
    id: string
    werkbonNumber: string
    type: WerkbonType
    status: WerkbonStatus
    weekNr: string
    order: {
      id: string
      orderNumber: string
      subject: string
    } | null
    client: {
      id: string
      name: string
    } | null
    clientLocation: {
      id: string
      name: string
    } | null
  }
  hasVariance: boolean
  variance: {
    minutesDelta: number
    kmDelta: number
    minutesPct: number
    kmPct: number
  } | null
  waitDays: number
}

// Werkbon-status vereenvoudiging (2026-05-27) — tab-counts splitsen de
// operationele status (nietGepland/gepland/afgerond) van de goedkeurings- en
// verwerk-statussen (wachtOpGoedkeuring/teFactureren/verloond).
export interface WerkbonTabCountsDTO {
  alle: number
  nietGepland: number
  gepland: number
  afgerond: number
  wachtOpGoedkeuring: number
  teFactureren: number
  verloond: number
}

export interface WerkbonMaterialDTO {
  id: string
  position: number
  description: string
  quantity: number
  unit: string | null
  unitPrice: number
  total: number
}

/**
 * Bijlage (foto, PDF) bij een werkbon. Mirror van DeclaratieAttachmentDTO —
 * downloadUrl is computed door de API en wijst naar
 * `/api/v1/werkbonnen/:werkbonId/attachments/:id`.
 */
export interface WerkbonAttachmentDTO {
  id: string
  fileName: string
  fileSize: number
  mimeType: string
  downloadUrl: string
  createdAt: string
}

// FO-063 — geresolvede bezoeklocatie (adres + geocode) van een werkbon, voor
// het tonen van het volledige adres + de "Navigeer"-knop in de medewerker-app
// en de planning. Bron: dezelfde locatie-cascade die planning/reistijd gebruikt
// (cursus → OpleiderLocatie via de sessie/sessiedag; klant → ClientLocation;
// terugval op het vrije-tekst-`locatie`-veld als label). `null`-velden = onbekend;
// is `adres`, `lat`/`lng` én `label` alles `null`, dan is er geen bekende locatie.
export interface WerkbonBezoekLocatie {
  /** Naam van de locatie (opleider-/klant-locatie); `null` bij enkel vrije tekst. */
  label: string | null
  /** Volledig bezoekadres "straat huisnr, postcode plaats"; `null` als onbekend. */
  adres: string | null
  postcode: string | null
  huisnummer: string | null
  /** Geocode voor de navigatie-deeplink (coords > adres voor nauwkeurigheid). */
  lat: number | null
  lng: number | null
  placeId: string | null
}

// FO-063 — per-dag bezoeklocatie voor een meerdaagse cursus met een afwijkende
// sessiedag-locatie (sessiedag-override). Alleen meegegeven wanneer de locatie
// daadwerkelijk per dag verschilt; anders volstaat de werkbon-brede
// `bezoekLocatie`.
export interface WerkbonBezoekLocatieDag {
  /** Kalenderdag in YYYY-MM-DD (Europe/Amsterdam-dag van de dag-entry). */
  datum: string
  locatie: WerkbonBezoekLocatie
}

export interface WerkbonDTO {
  id: string
  werkbonNumber: string
  // Ronde 4 W6 — `orderId` verwijderd; werkbon koppelt direct aan client.
  clientId: string
  projectLeaderId: string | null
  // Ronde 4 W6 — gemigreerd van Order: priority + contactpersoon + deadline.
  priority: WerkbonPriority
  clientContactId: string | null
  deadline: string | null
  // Ronde 4 W6b (spec 06b §1.2) — optionele koppeling naar de bron-offerte.
  // `quote` wordt alleen door de detail-response (GET /werkbonnen/:id) gevuld;
  // in de lijst-DTO volstaat `quoteId`.
  quoteId: string | null
  quote: { id: string; quoteNumber: string; status: QuoteStatus } | null
  type: WerkbonType
  status: WerkbonStatus
  factuurType: FactuurType
  // A7 — facturabel ja/nee (overrulebaar). B6 — vervangingsbehoefte op de
  // werkbon. Optioneel zodat clients die ze (nog) niet lezen niet breken.
  facturabel?: boolean
  /** batch-63 (FO-474) — waarom niet facturabel (null bij een facturabele bon). */
  facturabelReden?: WerkbonNietFacturabelReden | null
  /**
   * batch-63 (FO-474) — het afgeleide facturatie-spoor uit de gedeelde resolver
   * (zoals op de lijstrij); het detail-endpoint levert het mee, zodat "niet te
   * factureren" overal mét reden getoond wordt.
   */
  facturatie?: WerkbonFacturatieDTO
  /**
   * batch-65 (FO-484 §C.7) — de verzamelbon waar deze bon (nog) in zit, of null.
   * Alleen in de backoffice; `verzoekId` alleen met FACTUREN.canView.
   */
  bundel?: WerkbonBundelRefDTO | null
  vervangingsStatus?: VervangingStatus
  uurtarief: number
  overuurtarief: number
  startDatum: string
  eindDatum: string
  weekNr: string
  dienstType: DienstType
  ploegSoort: PloegSoort | null
  omschrijving: string | null
  referentie: string | null
  opmerkingen: string | null
  invoiceId: string | null
  // Werkbon-status vereenvoudiging (2026-05-27) — twee-staps goedkeuringsflow +
  // verlonings-/klant-akkoord-velden. Optioneel zodat clients die ze (nog) niet
  // lezen niet breken. `ingediendOp`/`ingediendById` zijn weg (geen handmatige
  // indiening meer; de operationele status wordt automatisch berekend).
  goedkeuringStatus?: WerkbonGoedkeuringStatus | null
  isVerwerktInVerloning?: boolean
  /** null = volg de klant-modus (Client.klantGoedkeuring === 'WERKBON'); boolean overrult per werkbon. */
  klantAkkoordVereistOverride?: boolean | null
  parentWerkbonId: string | null
  // Spec NN §3.2.1C — bron-koppeling voor werkbonnen die uit een kalender-event
  // of cursus-sessie zijn gegenereerd (auditspoor + idempotente generator).
  // sourceOpleidingDeelnameId is uniek: max. 1 werkbon per deelname. Optioneel
  // zodat clients die de bron niet lezen niet breken.
  sourceKalenderEventId?: string | null
  sourceOpleidingSessieId?: string | null
  sourceOpleidingDeelnameId?: string | null
  /** Leesbaar back-link-label naar de bron (bv. "Cursus: VCA-B" of meeting-titel). */
  bronLabel?: string | null
  bronType?: 'CURSUS' | 'KALENDER' | null
  dayEntries: WerkbonDayEntryDTO[]
  materials: WerkbonMaterialDTO[]
  totaalUren: number
  totaalOveruren: number
  totaalReisKm: number
  totaalReisMinuten: number
  totaalReisVergoeding: number
  goedgekeurdOp: string | null
  goedgekeurdBy: { id: string; name: string | null } | null
  afgekeurdRedenen: DayRejectReason[]
  afgekeurdToelichting: string | null
  afgekeurdOp: string | null
  afgekeurdBy: { id: string; name: string | null } | null
  // PR-4 (spec 02 §2.1) — consignatie-vlag + snapshot-tarieven.
  isConsignatie: boolean
  consignatieType: ConsignatieType | null
  consignatieRoosterId: string | null
  consignatieVastBedrag: number | null
  consignatiePerNacht: number | null
  consignatieOproepTarief: number | null
  consignatieMinimumUren: number | null
  // PR-J (spec 15 §2.2) — reistijd-override per werkbon (cascade-top).
  reistijdMaxKmOverride: number | null
  reistijdMaxMinOverride: number | null
  // PR-J (spec 15 §4.2) — klant-/vertegenwoordiger-aftekening.
  handtekeningData: string | null
  handtekeningNaam: string | null
  handtekeningFunctie: string | null
  handtekeningOp: string | null
  handtekeningLatitude: number | null
  handtekeningLongitude: number | null
  // PR-J (spec 15 §4.3) — digitaal accorderen (alternatief voor handtekening).
  geaccordeerdDoorKlant: boolean
  geaccordeerdDoorKlantNaam: string | null
  geaccordeerdOp: string | null
  // FO-062 (batch-19) — per-monteur gereedmeld-status op een GEDEELDE werkbon.
  // Alleen aanwezig wanneer de bon dayEntries van >= 2 monteurs heeft (anders
  // ongewijzigd één-monteur-gedrag). Additief — clients die dit niet lezen breken niet.
  gereedmeldStatus?: WerkbonGereedmeldStatusDTO
}

/** FO-062 — gereedmeld-status van één toegewezen monteur op een gedeelde werkbon. */
export interface WerkbonGereedMonteurDTO {
  employeeId: string
  naam: string
  gereed: boolean
  /** ISO-tijdstip van de gereedmelding, of null zolang nog niet gereed. */
  gereedmeldOp: string | null
}

/**
 * FO-062 — per-monteur gereedmeld-overzicht op een GEDEELDE werkbon (>= 2
 * monteurs). De werkbon-brede goedkeuringsflow start pas als `alleGereed`. Voor
 * een één-monteur-werkbon wordt dit niet meegestuurd.
 */
export interface WerkbonGereedmeldStatusDTO {
  gedeeld: true
  monteurs: WerkbonGereedMonteurDTO[]
  alleGereed: boolean
  /** Monteurs die nog niet hun deel gereedmeldden (subset van `monteurs`). */
  nogNietGereed: WerkbonGereedMonteurDTO[]
}

/**
 * Canonieke werkbon-audit-actie-strings. Single source of truth — wordt
 * geïmporteerd in `apps/api/src/lib/werkbon-audit.ts` zodat backend en types
 * niet kunnen driften.
 *
 * Twee groepen:
 *   - PR-2 (werkbon-niveau goedkeuring): INGEDIEND/GOEDGEKEURD/AFGEKEURD/
 *     HEROPEND/GEFACTUREERD/TERUG_NAAR_CONCEPT/WERKBON_UPDATED.
 *   - Legacy (vóór PR-2 refactor): WERKBON_CREATED/WERKBON_STATUS_TRANSITION/
 *     DAY_ENTRY_STATUS_TRANSITION/DAY_ENTRY_HEROPEN/WERKBON_BULK_REJECT/
 *     INVOICE_CREATED. Kunnen nog voorkomen in audit-rows die vóór de
 *     migratie zijn aangemaakt; de UI/types moeten ze blijven accepteren.
 */
export const WERKBON_AUDIT_ACTIONS = [
  // PR-2 (werkbon-refactor)
  'INGEDIEND',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'HEROPEND',
  'GEFACTUREERD',
  'TERUG_NAAR_CONCEPT',
  'WERKBON_UPDATED',
  // Factuur-flow (gegenereerd door apps/api/src/routes/invoices.ts)
  'INVOICED',
  'CREATED',
  'DELETED',
  'INVOICE_DELETED',
  // batch-60 facturen-verwijderen (FO-443) - herstel van een verwijderde factuur.
  'INVOICE_RESTORED',
  'VERSTUUR_NAAR_KLANT',
  // Planning-flow (gegenereerd door apps/api/src/routes/planning.ts)
  'REASSIGN',
  'DAY_ENTRY_MOVED',
  'DAY_ENTRY_UNASSIGNED',
  // batch-52 in-te-plannen (FO-398 §6.4) — annuleren van toekomstige dagen bij
  // uitdienst/ziekte/verlof (soft-delete met verplichte reden). Stond niet in
  // deze lijst, waardoor de werkbon-tijdlijn de rauwe enum-string toonde.
  'DAY_ENTRIES_GEANNULEERD',
  // Spec 110 §3.4 — de prullenbak van het vak "In te plannen": onbemande dagen
  // die de planner definitief laat vervallen. Bewust een eigen actie en niet
  // DAY_ENTRIES_GEANNULEERD: die hoort bij de uitdienst-/ziekte-/verlofdialoog
  // en draagt een reden; deze komt uit het planbord en heeft er geen.
  'IN_TE_PLANNEN_VERWIJDERD',
  // Ronde 4 W1 PF3 — planning-conflict bewust geaccepteerd via `force: true`
  // in reassign / move-day-entry / werkbon-create. Audit-rij bevat de
  // geserialiseerde conflict-lijst in `metadata.conflicts`.
  'CONFLICT_ACCEPTED',
  // Ronde 4 W8 (spec ronde-4/08 §3.3) — planner plant medewerker boven
  // capaciteit in via `force: true` + verplichte `overruleReason`. Audit-rij
  // bevat de reden + bezettingsgraad-snapshot in `metadata`.
  'CAPACITY_OVERRULE',
  // PR-4 consignatie (spec 02 §3 + §4.2 + master-prompt)
  'CREATED_CONSIGNATIE',
  'OPROEP_TOEGEVOEGD',
  // FO-105 (batch-21) — correctie van een geboekte oproep (tijd/reis).
  'OPROEP_GEWIJZIGD',
  'OPROEP_VERWIJDERD',
  // Werkbon-bijlagen (foto's / PDF's): toegevoegd door monteur via portal of
  // backoffice. Apart van WERKBON_UPDATED zodat audit-filtering eenvoudig is.
  'BIJLAGE_TOEGEVOEGD',
  'BIJLAGE_VERWIJDERD',
  // Legacy (vóór PR-2 refactor — kunnen voorkomen in oudere audit-rows)
  'WERKBON_CREATED',
  'WERKBON_STATUS_TRANSITION',
  'DAY_ENTRY_STATUS_TRANSITION',
  'DAY_ENTRY_HEROPEN',
  'WERKBON_BULK_REJECT',
  'INVOICE_CREATED',
  // Spec A §SS14.1 — werkbon-modificatie + reistijd-override + display-name
  'REISTIJD_OVERRIDE',
  'MEDEWERKER_VERVANGEN',
  'EXTRA_INZET',
  'SCOPE_WIJZIGING',
  'DISPLAY_NAME_BIJGEWERKT',
  // Ronde 4 W2 (spec 02 §2.5) — wijzig medewerker op de hele reeks
  // (parent + alle toekomstige CONCEPT-children). Apart van REASSIGN (één
  // werkbon) zodat reeks-wijzigingen direct herkenbaar zijn in de audit-log.
  'REEKS_MEDEWERKER_GEWIJZIGD',
  // Ronde 4 W4 (spec 04 §2.4) — verloningsbeveiliging-acties.
  // Status-transitie naar VERWERKT_IN_VERLONING.
  'VERWERKT_IN_VERLONING',
  // Payroll-correctie levensloop (separate van VERWERKT_IN_VERLONING zodat de
  // audit-timeline duidelijk laat zien dat dit géén werkbon-status-transitie
  // is maar een correctie-event).
  'PAYROLL_CORRECTIE_AANGEMAAKT',
  'PAYROLL_CORRECTIE_GOEDGEKEURD',
  'PAYROLL_CORRECTIE_AFGEWEZEN',
  'PAYROLL_CORRECTIE_VERWERKT',
  // Periode-slot beheer. Wordt geschreven op het werkbon-niveau wanneer een
  // werkbon valt in een PayrollLock-periode op het moment van afsluiten.
  'PAYROLL_PERIODE_GESLOTEN',
  'PAYROLL_PERIODE_HEROPEND',
  // Revisie-event (niet status-gebonden) — geschreven als sidecar bij elke
  // createWerkbonRevision()-aanroep zodat audit + revision-historie 1-op-1
  // gekoppeld zijn.
  'WERKBON_REVISION_CREATED',
  // Werkbon-status vereenvoudiging (2026-05-27) — interne goedkeuringsflow +
  // automatische operationele-status-herberekening.
  'GOEDGEKEURD_INTERN',
  'AFGEKEURD_INTERN',
  'STATUS_AUTO_UPDATE',
  // Uren-herleiding (2026-06-01) — medewerker meldt zijn werkbon expliciet
  // gereed (volledigheidscontrole + start van de goedkeuringsflow), ook vóór
  // het verstrijken van de laatste geplande dag.
  'GEREEDGEMELD',
  // Finqle-verzenden (2026-06-04) — werkbon-PDF per e-mail naar het Finqle-
  // parse-adres van de BV. Geen operationele status-transitie (alleen verzend-
  // markering op de werkbon).
  'VERZONDEN_NAAR_FINQLE',
  // Werkbon-naamgeving bij omplannen (2026-06-10) — omschrijving/referentie
  // automatisch hernoemd nadat alle (resterende) dagen naar een andere
  // medewerker gingen. metadata bevat de oude en nieuwe waarden.
  'NAAMGEVING_BIJGEWERKT',
  // Opleiding-werkbon-cleanup (batch-8 opleiding) — een uit een cursus-sessie
  // gegenereerde interne werkbon wordt opgeruimd bij annuleren/afmelden/vervangen:
  //   CURSUS_GEANNULEERD = onaangeraakte bon volledig soft-deleted (van planbord).
  //   CURSUS_INGEKORT     = bon met al-gewerkte/vergrendelde dagen; alleen de
  //                         toekomstige geplande cursusdagen verwijderd, bon blijft.
  'CURSUS_GEANNULEERD',
  'CURSUS_INGEKORT',
  // Uren namens monteur (2026-07-19) — backoffice wijzigt de werkelijke uren
  // (actuals/pauze/afwezig) op de dag van een andere medewerker, bv. voor een
  // uit-dienst-medewerker die zijn week niet meer kan invullen. metadata bevat
  // de oude en nieuwe waarden per dag; note de (door de UI verplichte) reden.
  'UREN_GEWIJZIGD_BACKOFFICE',
  // batch-63 pauze-tweeassig (FO-473) - kantoor past de pauze per as aan
  // (verloning en/of factuurpauze, met reden) of de herrekening zet een open
  // dagregel op de nieuwe regel. metadata: dayEntryId + oud/nieuw per as.
  // (Midden in de lijst i.p.v. aan de staart: minder merge-botsingen.)
  'PAUZE_AANGEPAST',
  'PAUZE_HERREKEND',
  // FO-413 — BV (entiteit) gekoppeld of herkoppeld op de werkbon: handmatig via
  // de backoffice-koppel-dialoog of door de herstelrun. metadata bevat van/naar,
  // herkomst en of de bon al gefactureerd/naar Finqle verzonden was.
  'ENTITEIT_GEKOPPELD',
  // FO-429 (batch-58 profiel-match) - snapshot van de ZACHTE profielwens-score
  // op het moment van toewijzen. Puur registratie: deze rij markeert geen
  // blokkade en geen statuswissel (de reassign- en klant-drag-create-paden
  // hangen dezelfde snapshot aan hun eigen audit-rij).
  'PROFIEL_MATCH',
  // batch-60 vaste-monteur (FO-440 §A.2) - vaste monteur per slot afgeleid,
  // bevestigd, gewisseld of verwijderd. Een structurele wissel is een bewuste
  // wijziging: metadata bevat positie, van/naar, bron en de afleidingsbasis.
  'VASTE_MONTEUR_GEWIJZIGD',
  // batch-60 vaste-monteur (FO-440) - een dag is door de werkbon-split
  // ("één werkbon per monteur") naar de eigen bon van de monteur verhuisd.
  // metadata: dayEntryId + sourceWerkbonId + targetWerkbonId; de
  // bezettingsstatus-resolver leest hierop de dienst-herkomst terug.
  'DAY_ENTRY_AFGESPLITST',
  // batch-61 werkbon-gefactureerd (FO-449) - het facturatie-spoor naast de
  // operationele status. De Finqle-verzending zelf blijft VERZONDEN_NAAR_FINQLE
  // (met `facturatieStatus` in de metadata); deze vier dekken de overige paden.
  'GEFACTUREERD_GEMARKEERD',
  'GEFACTUREERD_TERUGGEDRAAID',
  'GEFACTUREERD_VIA_FINQLE_API',
  'FACTURATIE_VERVANGEN_DOOR_FACTUUR',
  // FO-032 (batch-16) - uit/in facturatie gehaald. Stonden al als losse strings
  // in de audit; batch-63 (FO-474) schrijft ze ook vanuit het bewerken van de
  // werkbon (metadata: reden + via), zodat "wanneer ging deze bon uit de
  // facturatie en waarom" overal uit dezelfde actie komt.
  'UIT_FACTURATIE_GEHAALD',
  'TERUG_IN_FACTURATIE',
  // batch-63 werkbon-facturatie (FO-474) - herstelscript: omgevallen
  // facturabel-vlag teruggezet of reden vastgelegd (metadata: runId, categorie,
  // voor/na), en het terugdraaien van zo'n run.
  'FACTURABEL_HERSTELD',
  'FACTURABEL_HERSTEL_TERUGGEDRAAID',
  // batch-65 integratie - de bon staat bij Finqle op een tweede factuur (een
  // tweede facturatieverzoek met een eigen factuur). Het nummer wordt dan niet
  // overschreven; dit eenmalige signaal per andere factuur maakt het zichtbaar.
  // metadata: huidige + andere referentie en Finqle-invoice-id's.
  'FINQLE_FACTUUR_CONFLICT',
  // batch-69 werkbon-pauze (FO-511 §C6, review 2) - de klantstap van een intern
  // goedgekeurde bon wachtte op de pauzeregel van de klant (GOEDGEKEURD_INTERN)
  // en is doorgezet naar de klant (metadata: naar, gateActief).
  'KLANTSTAP_VRIJGEGEVEN',
  // batch-69 werkbon-pauze (FO-511 §C7) - de factuurpauze van een werkbon in een
  // facturatieverzoek is gecorrigeerd naar de bevestigde pauzeregel; de werkbon
  // moet opnieuw naar Finqle (metadata: verzoek, dagen oud/nieuw).
  'FACTUURPAUZE_GECORRIGEERD',
] as const

export type WerkbonAuditAction = (typeof WERKBON_AUDIT_ACTIONS)[number]

/**
 * NL-labels per audit-action. Eén bron voor frontend-display (audit-timeline,
 * werkbon-detail, planning-conflict-audit). Onbekende actions vallen terug op
 * de raw string in de UI.
 */
export const WERKBON_AUDIT_ACTION_LABELS: Record<WerkbonAuditAction, string> = {
  // PR-2 werkbon-refactor
  INGEDIEND: 'Ingediend',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD: 'Afgekeurd',
  HEROPEND: 'Heropend',
  GEFACTUREERD: 'Gefactureerd',
  TERUG_NAAR_CONCEPT: 'Teruggezet naar concept',
  WERKBON_UPDATED: 'Werkbon bijgewerkt',
  // Factuur-flow
  INVOICED: 'Gefactureerd',
  CREATED: 'Aangemaakt',
  DELETED: 'Verwijderd',
  INVOICE_DELETED: 'Factuur verwijderd',
  INVOICE_RESTORED: 'Factuur hersteld',
  VERSTUUR_NAAR_KLANT: 'Verstuurd naar klant',
  // Planning-flow
  REASSIGN: 'Hertoegewezen',
  DAY_ENTRY_MOVED: 'Dag verplaatst',
  DAY_ENTRY_UNASSIGNED: 'Dag losgekoppeld',
  DAY_ENTRIES_GEANNULEERD: 'Dagen geannuleerd',
  IN_TE_PLANNEN_VERWIJDERD: 'Verwijderd uit In te plannen',
  CONFLICT_ACCEPTED: 'Conflict bewust geaccepteerd',
  // Ronde 4 W8 — capaciteits-overrule
  CAPACITY_OVERRULE: 'Capaciteit-overrule (boven bezetting ingepland)',
  // Consignatie
  CREATED_CONSIGNATIE: 'Consignatie aangemaakt',
  OPROEP_TOEGEVOEGD: 'Oproep toegevoegd',
  OPROEP_GEWIJZIGD: 'Oproep gewijzigd',
  OPROEP_VERWIJDERD: 'Oproep verwijderd',
  // Bijlagen
  BIJLAGE_TOEGEVOEGD: 'Bijlage toegevoegd',
  BIJLAGE_VERWIJDERD: 'Bijlage verwijderd',
  // Legacy (pre PR-2)
  WERKBON_CREATED: 'Werkbon aangemaakt',
  WERKBON_STATUS_TRANSITION: 'Status gewijzigd',
  DAY_ENTRY_STATUS_TRANSITION: 'Dagstatus gewijzigd',
  DAY_ENTRY_HEROPEN: 'Dag heropend',
  WERKBON_BULK_REJECT: 'Bulkafkeuring',
  INVOICE_CREATED: 'Factuur aangemaakt',
  // Spec A §SS14.1
  REISTIJD_OVERRIDE: 'Reistijd-override',
  MEDEWERKER_VERVANGEN: 'Medewerker vervangen',
  EXTRA_INZET: 'Extra inzet',
  SCOPE_WIJZIGING: 'Scope-wijziging',
  DISPLAY_NAME_BIJGEWERKT: 'Weergavenaam bijgewerkt',
  // Ronde 4 W2 — reeks-medewerker wijziging
  REEKS_MEDEWERKER_GEWIJZIGD: 'Reeks-medewerker gewijzigd',
  // Ronde 4 W4 — verloningsbeveiliging
  VERWERKT_IN_VERLONING: 'Verwerkt in verloning',
  PAYROLL_CORRECTIE_AANGEMAAKT: 'Payroll-correctie aangemaakt',
  PAYROLL_CORRECTIE_GOEDGEKEURD: 'Payroll-correctie goedgekeurd',
  PAYROLL_CORRECTIE_AFGEWEZEN: 'Payroll-correctie afgewezen',
  PAYROLL_CORRECTIE_VERWERKT: 'Payroll-correctie verwerkt',
  PAYROLL_PERIODE_GESLOTEN: 'Verloningsperiode gesloten',
  PAYROLL_PERIODE_HEROPEND: 'Verloningsperiode heropend',
  WERKBON_REVISION_CREATED: 'Revisie opgeslagen',
  // Werkbon-status vereenvoudiging (2026-05-27)
  GOEDGEKEURD_INTERN: 'Intern goedgekeurd',
  AFGEKEURD_INTERN: 'Intern afgekeurd',
  STATUS_AUTO_UPDATE: 'Status automatisch bijgewerkt',
  // Uren-herleiding (2026-06-01)
  GEREEDGEMELD: 'Gereedgemeld',
  // Finqle-verzenden (2026-06-04)
  VERZONDEN_NAAR_FINQLE: 'Verzonden naar Finqle',
  NAAMGEVING_BIJGEWERKT: 'Naamgeving bijgewerkt na medewerker-wissel',
  // Opleiding-werkbon-cleanup (batch-8 opleiding)
  CURSUS_GEANNULEERD: 'Cursus-werkbon opgeruimd',
  CURSUS_INGEKORT: 'Cursus-werkbon ingekort',
  // Uren namens monteur (2026-07-19)
  UREN_GEWIJZIGD_BACKOFFICE: 'Uren ingevuld/gewijzigd door backoffice',
  // batch-63 pauze-tweeassig (FO-473)
  PAUZE_AANGEPAST: 'Pauze aangepast (verloning/factuur)',
  PAUZE_HERREKEND: 'Pauze herrekend (nieuwe pauzeregel)',
  // FO-413
  ENTITEIT_GEKOPPELD: 'BV gekoppeld',
  // FO-429 (batch-58 profiel-match)
  PROFIEL_MATCH: 'Profielwens klant vastgelegd bij toewijzing',
  VASTE_MONTEUR_GEWIJZIGD: 'Vaste monteur gewijzigd',
  DAY_ENTRY_AFGESPLITST: 'Dag afgesplitst naar eigen bon',
  // batch-61 werkbon-gefactureerd (FO-449)
  GEFACTUREERD_GEMARKEERD: 'Gemarkeerd als gefactureerd',
  GEFACTUREERD_TERUGGEDRAAID: 'Markering gefactureerd teruggedraaid',
  GEFACTUREERD_VIA_FINQLE_API: 'Gefactureerd via Finqle (factuur)',
  FACTURATIE_VERVANGEN_DOOR_FACTUUR: 'Handmatige markering vervangen door factuur',
  // FO-032 + batch-63 (FO-474)
  UIT_FACTURATIE_GEHAALD: 'Uit facturatie gehaald',
  TERUG_IN_FACTURATIE: 'Terug in facturatie',
  FACTURABEL_HERSTELD: 'Facturabel-vlag hersteld',
  FACTURABEL_HERSTEL_TERUGGEDRAAID: 'Herstel facturabel-vlag teruggedraaid',
  // batch-65 integratie
  FINQLE_FACTUUR_CONFLICT: 'Staat bij Finqle op meer dan één factuur',
  KLANTSTAP_VRIJGEGEVEN: 'Klantstap vrijgegeven (naar de klant of goedgekeurd)',
  FACTUURPAUZE_GECORRIGEERD: 'Factuurpauze gecorrigeerd (opnieuw aanleveren)',
}

// ─── FO-413 — BV (entiteit) op de werkbon koppelen/herstellen ───────────────

/** Foutcodes van de werkbon-BV-keten (API ↔ backoffice). */
export const WERKBON_ENTITEIT_ERROR_CODES = {
  /** Aanmaak: de BV kan niet bepaald worden (type niet aan een BV gekoppeld). */
  ONBEPAALD: 'WERKBON_ENTITEIT_ONBEPAALD',
  /** Finqle-verzending: de werkbon heeft (nog) geen BV. */
  ONTBREEKT: 'WERKBON_ENTITEIT_ONTBREEKT',
  /** Koppelen: de gekozen BV bestaat niet of is niet actief. */
  ONGELDIG: 'WERKBON_ENTITEIT_ONGELDIG',
  /** Koppelen: gefactureerd/Finqle-verzonden → expliciete bevestiging vereist. */
  BEVESTIGING_VEREIST: 'WERKBON_ENTITEIT_BEVESTIGING_VEREIST',
} as const

/** Hoe de BV van een werkbon is bepaald (voorstel in de koppel-dialoog + audit). */
export type WerkbonEntiteitHerkomst =
  | 'EXPLICIET'
  | 'INTERNE_KLANT'
  | 'TYPE_MAPPING'
  // FO-420 (batch-56) — klant-specifieke tredes, ná de type-koppeling zodat de
  // BV-routing van bestaande klanten niet stil verandert (zie werkbon-entiteit.ts).
  | 'KLANT_TARIEF'
  | 'KLANT_DEFAULT'

export const WERKBON_ENTITEIT_HERKOMST_LABELS: Record<WerkbonEntiteitHerkomst, string> = {
  EXPLICIET: 'expliciet gekozen',
  INTERNE_KLANT: 'via de interne klant',
  TYPE_MAPPING: 'via de werkbon-type-koppeling',
  KLANT_TARIEF: 'via de tariefafspraak van de klant',
  KLANT_DEFAULT: 'via de standaard-BV van de klant',
}

export interface WerkbonEntiteitOptieDTO {
  id: string
  naam: string
  /** Heeft deze BV een Finqle-parse-adres (nodig voor verzenden naar Finqle)? */
  heeftFinqleAdres: boolean
}

/** `GET /werkbonnen/:id/entiteit-opties` — voeding van de koppel-dialoog. */
export interface WerkbonEntiteitOptiesDTO {
  huidige: { id: string; naam: string } | null
  /** De BV die de aanmaak-resolutie zou kiezen (voorgeselecteerd in de dialoog). */
  voorstel: { id: string; naam: string; herkomst: WerkbonEntiteitHerkomst } | null
  /** Waarom er geen voorstel is (bv. type niet aan een BV gekoppeld). */
  voorstelReden: string | null
  /**
   * FO-420 (batch-56) — de BV's die de tariefafspraken van deze klant
   * aanwijzen. Bij meerdere kiest de cascade bewust niet (§5.1); de dialoog zet
   * ze bovenaan de picker zodat de juiste keuze één klik is.
   */
  kandidaatIds: string[]
  opties: WerkbonEntiteitOptieDTO[]
  /** Aantal werkbonnen (hoofdbon + weekbonnen) dat bij koppelen mee wijzigt. */
  reeksAantal: number
  /** Gefactureerd/Finqle-verzonden → de gebruiker moet expliciet bevestigen. */
  bevestigingVereist: boolean
  bevestigingRedenen: string[]
}

/** `PATCH /werkbonnen/:id/entiteit` — resultaat van (her)koppelen. */
export interface WerkbonEntiteitKoppelResultDTO {
  entity: { id: string; naam: string }
  van: { id: string; naam: string } | null
  /** Aantal daadwerkelijk gewijzigde werkbonnen (0 = stond al op deze BV). */
  gewijzigd: number
  werkbonIds: string[]
}

export interface WerkbonAuditEntryDTO {
  id: string
  werkbonId: string
  action: WerkbonAuditAction | string
  // Werkbon-status vereenvoudiging (2026-05-27) — vrije tekst (TEXT-kolom);
  // historische waarden als CONCEPT/INGEDIEND kunnen voorkomen.
  fromStatus: string | null
  toStatus: string | null
  performedBy: string
  performedByName: string | null
  note: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
}

/**
 * Multi-monteur naamgeving: per EXTRA monteur (`employeeIds.slice(1)`) de
 * werkbon-omschrijving/referentie, zodat elke monteur z'n eigen naam in de
 * auto-omschrijving (`${jaar} - ${klant} - ${dienst} - ${voornaam}`) krijgt
 * i.p.v. die van de primaire monteur. De primaire monteur gebruikt de gewone
 * `omschrijving`/`referentie`. Een ontbrekend veld → de clone erft de primaire
 * waarde.
 */
export interface WerkbonEmployeeNaming {
  employeeId: string
  omschrijving?: string | null
  referentie?: string | null
}

export interface CreateWerkbonInput {
  clientId: string
  defaultEmployeeId?: string | null
  /** Bug #1 — multi-monteur: 1 werkbon per monteur met gedeelde planningGroupId. Eerste id = primair. */
  employeeIds?: string[]
  /** Multi-monteur: per extra monteur z'n eigen omschrijving/referentie (eigen naam). */
  employeeNaming?: WerkbonEmployeeNaming[]
  projectLeaderId?: string | null
  // Ronde 4 W6 — Order-velden gemigreerd naar de werkbon zelf.
  clientContactId?: string | null
  priority?: WerkbonPriority
  deadline?: string | null
  // Ronde 4 W6b (spec 06b §1.2) — optionele bron-offerte. Alleen gezet via de
  // deep-link vanuit offerte-detail; backend valideert tegen prospect-quote.
  quoteId?: string | null
  type: WerkbonType
  factuurType: FactuurType
  // A7 — facturabel-override; afwezig => server leidt af uit de klant (intern -> false).
  // Alleen meesturen als het veld bewust is aangeraakt (FO-474).
  facturabel?: boolean
  /** batch-63 (FO-474) — verplicht bij `facturabel: false` (formulier-redenen, zonder OVERIG). */
  facturabelReden?: WerkbonNietFacturabelRedenFormulier | null
  // B6 — vervangingsbehoefte-override; afwezig => server leidt af uit het pakket.
  vervangingsStatus?: VervangingStatus
  uurtarief: number
  startWeek?: string
  dagenPerWeek?: number
  weekdays?: number[]
  aantalWeken?: number
  geplandUrenPerDag: number
  omschrijving?: string | null
  referentie?: string | null
  /** BLOK 4: snapshot van een bestaand klant-tarief. */
  tariffId?: string
  /** BLOK 4: snapshot van een bestaand werkschema. */
  workScheduleId?: string
  // §5 — planmodus is optioneel voor backwards-compat; default 'weken' op server.
  planModus?: PlanModus
  // Bij planModus='datumbereik'
  startDatum?: DateISO
  eindDatum?: DateISO
  // Bij planModus='losse-dagen'
  dagen?: DateISO[]
  // DAGDIENST (single-create): de DAG-diensttijden + pauze die de gebruiker
  // invult. Zonder deze viel de werkbon terug op hardcoded 08:00–17:00/60.
  // Ploegen lopen via CreateWerkbonBulkInput.dienstTijden.
  plannedStart?: string
  plannedEnd?: string
  pauzeMinutes?: number
  // Ronde 4 W1 PF3 — bewuste override van een planning-conflict (dubbele
  // inplanning / ziekte / training). Bij `true` slaat de API de 409
  // PLANNING_CONFLICT over en legt een CONFLICT_ACCEPTED audit-rij vast.
  force?: boolean
  // Batch-6 cert §5b — certificaat-screening: waarschuwingen accepteren /
  // blokkade overrulen met reden (PLANNING.canApprove, audit + HR-melding).
  certAkkoord?: boolean
  certOverrideReden?: string
  /**
   * Reisuren-berekening uitzetten op gegenereerde dag-entries. 'ALLE' = alle
   * dagen van deze werkbon; `{ dagen }` = alleen die yyyy-mm-dd-datums. Afwezig
   * = gewoon berekenen. De server is autoritair (past de vlag alleen toe op
   * dag-entries die hij daadwerkelijk genereert).
   */
  geenReisuren?: 'ALLE' | { dagen: string[] }
  /**
   * FO-008 — besloten werkbon + de gekozen medewerkers (Employee.id's). De maker
   * (createdById) + de toegewezen monteurs zien de inhoud altijd; de gekozen lijst
   * default op de toegewezen monteurs. Server-side afscherming (CC.SCOPE). Voor een
   * bestaande werkbon (ook ploegen) loopt het beheer via PATCH /werkbonnen/:id/besloten.
   */
  besloten?: boolean
  beslotenEmployeeIds?: string[]
}

export interface CreateWerkbonBulkInput {
  clientId: string
  defaultEmployeeId?: string | null
  /** Bug #1 — multi-monteur: 1 werkbon per monteur met gedeelde planningGroupId. Eerste id = primair. */
  employeeIds?: string[]
  /** Multi-monteur: per extra monteur z'n eigen omschrijving/referentie (eigen naam). */
  employeeNaming?: WerkbonEmployeeNaming[]
  projectLeaderId?: string | null
  // Ronde 4 W6 — Order-velden gemigreerd naar de werkbon zelf.
  clientContactId?: string | null
  priority?: WerkbonPriority
  deadline?: string | null
  // Ronde 4 W6b (spec 06b §1.3) — bron-offerte; wordt op parent én alle
  // children gezet (consistent met andere structurele velden).
  quoteId?: string | null
  type: WerkbonType
  factuurType: FactuurType
  /** A7/B6 (FIX 3) - facturabel + vervangingsbehoefte ook op het ploegen/bulk-
   *  pad. Weggelaten => server leidt de defaults af (klant + servicepakket).
   *  Alleen meesturen als het veld bewust is aangeraakt (FO-474). */
  facturabel?: boolean
  /** batch-63 (FO-474) — verplicht bij `facturabel: false` (formulier-redenen, zonder OVERIG). */
  facturabelReden?: WerkbonNietFacturabelRedenFormulier | null
  vervangingsStatus?: VervangingStatus
  uurtarief: number
  startWeek: string
  dagenPerWeek?: number
  weekdays?: number[]
  geplandUrenPerDag: number
  omschrijving?: string | null
  referentie?: string | null
  eindWeek: string
  frequentieWeken: number
  dienstType: Exclude<DienstType, 'DAGDIENST'>
  ploegRotatie: PloegSoort[]
  dienstTijden: Record<PloegSoort, { start: string; eind: string }>
  /**
   * FO-473 - weggelaten = volgens afspraak (werkschema per dienst, klant,
   * systeemstandaard); een waarde = de pauze die de planner koos (FORMULIER).
   */
  pauzeMinutes?: number
  /** BLOK 4: snapshot van een bestaand klant-tarief. */
  tariffId?: string
  /** BLOK 4: snapshot van een bestaand werkschema. */
  workScheduleId?: string
  // §5 / #48 — ploegen kan nu 'weken' (default) óf 'losse-dagen'; bij losse-dagen
  // stuurt de modal de gekozen dagen mee en leidt de server de ploegsoort per dag
  // uit het rooster af. startWeek/eindWeek blijven het week-anker.
  planModus?: PlanModus
  /** #48 — gekozen dagen (yyyy-mm-dd) bij planModus='losse-dagen'. */
  losseDagen?: string[]
  /**
   * PR-D1 (spec ploegendienst-zondagnacht-fix §2.6) — Bij nachtdienst-roosters
   * die op zondagnacht starten: behandel zondag (offset 6) als de zondag VOOR
   * de werkweek (monday - 1) i.p.v. zondag NA (monday + 6).
   */
  zondagnachtAlsWerkweekStart?: boolean
  /**
   * Spec 07 — dag-rotatie (DayCode-pattern). Wint per dag van ploegRotatie.
   * Rooster-flex (Q21): fallback-bron voor het patroon wanneer er GÉÉN werkschema
   * gekoppeld is; bij een gekoppeld werkschema komt het patroon serverside altijd
   * daaruit (geen patroon-override per werkbon).
   */
  rotation?: { pattern: string[]; cycleDays: number; startDate: string }
  /**
   * Rooster-werkschema-flexibiliteit (PO Q20/Q21) — startpositie in het
   * rotatiepatroon: pattern[offset] valt op de eerste werkbondag. Eén persistent
   * veld; vervangt het oude `rotation.startOffset` + de `overrideRotatie`-vlag.
   * Het werkschema is de enige bron voor het patroon.
   */
  rotationPatternOffset?: number
  /**
   * Ronde 4 W2 (spec 02 §2.7) — reistijd-max overrides die ook bij
   * bulk-create per child gekopieerd moeten worden. Volgen de cascade
   * werkbon > klant > globaal uit PR-J (spec 15 §2.2).
   */
  reistijdMaxKmOverride?: number | null
  reistijdMaxMinOverride?: number | null
  // Ronde 4 W1 PF3 — bewuste override van een planning-conflict (zie
  // CreateWerkbonInput.force). Bij `true` slaat de bulk-route de 409
  // PLANNING_CONFLICT over en legt CONFLICT_ACCEPTED audit-rijen vast.
  force?: boolean
  // Batch-6 cert §5b — certificaat-screening (zie CreateWerkbonInput).
  certAkkoord?: boolean
  certOverrideReden?: string
  /**
   * Reisuren-berekening uitzetten op gegenereerde dag-entries. 'ALLE' = alle
   * dagen van deze werkbon; `{ dagen }` = alleen die yyyy-mm-dd-datums. Afwezig
   * = gewoon berekenen. De server is autoritair (past de vlag alleen toe op
   * dag-entries die hij daadwerkelijk genereert).
   */
  geenReisuren?: 'ALLE' | { dagen: string[] }
}

export interface ClientTariffDTO {
  packageType: PackageType | null
  hourlyRate: number | null
  shiftType: ShiftType | null
  shiftSurchargePct: number | null
  /** PR-H (spec 08 §2.3) — toeslag per uur in €; null = geen toeslag. */
  packageSurchargeAmount: number | null
  invoicingEntityId: string | null
  invoiceEmail: string | null
}

/**
 * BLOK 4: kompacte tariff-row voor de werkbon-create dropdown.
 * Bevat ook overtimeRate zodat de UI bij selectie het overuurtarief
 * kan voorvullen.
 */
export interface ClientTariffOptionDTO {
  id: string
  name: string
  isDefault: boolean
  packageType: PackageType | null
  hourlyRate: number | null
  overtimeRate: number | null
  shiftType: ShiftType | null
  shiftSurchargePct: number | null
  /** PR-H (spec 08 §2.3) — toeslag per uur in €; null = geen toeslag. */
  packageSurchargeAmount: number | null
  invoicingEntityId: string | null
  invoiceEmail: string | null
}

/**
 * BLOK 4: kompacte work-schedule row voor de werkbon-create dropdown.
 *
 * PR-H (spec 08 §1.5) — uitgebreid met `diensten`, `rotationPattern`,
 * `rotationStartDate` en `rotationCycleDays` zodat de werkbon-create modal
 * deze velden bij werkschema-selectie automatisch kan invullen.
 */
export interface ClientWorkScheduleDienstDTO {
  type: PloegSoort
  startTime: string
  endTime: string
  breakMinutes: number
  /** FO-473 - geresolvede verloning van deze dienst (geschreven/doorbetaald). */
  pauzeVerloning?: 'GESCHREVEN' | 'DOORBETAALD'
  /** FO-473 - geresolvede factuurpauze van deze dienst. */
  pauzeFacturatieMinuten?: number
  /**
   * FO-473 (batch-63) - de afspraak zoals hij OP de dienst staat (`null` per
   * veld = geen eigen afspraak). `breakMinutes` hierboven is al geresolved en
   * kan dus uit de klant- of systeemstandaard komen; een preview die hem als
   * dienst-afspraak voert, toont de verkeerde herkomst.
   */
  pauzeAfspraak?: {
    minuten: number | null
    verloning: 'GESCHREVEN' | 'DOORBETAALD' | null
    facturatieMinuten: number | null
  }
}

/**
 * FO-061 (batch-18) — per-datum afwijking op het werkschema-rooster (gesloten of
 * tijd-/pauze-override). Structureel gelijk aan `DienstUitzondering`
 * (`@streep/utils`); hier inline gedefinieerd omdat `@streep/types` niet van
 * `@streep/utils` mag afhangen (circulair). De werkbon-create-modal voert dit aan
 * de dienst-resolver zodat de voorgevulde "volgens rooster"-dienst ook de
 * per-datum-uitzondering respecteert (zoals de server bij het plannen doet).
 */
export interface ClientWorkScheduleUitzonderingDTO {
  datum: string
  gesloten?: boolean
  startTime?: string
  endTime?: string
  breakMinutes?: number
  /** FO-473 - afwijkende verloning/factuurpauze voor die datum. */
  pauzeVerloning?: 'GESCHREVEN' | 'DOORBETAALD'
  pauzeFacturatieMinuten?: number
}

export interface ClientWorkScheduleOptionDTO {
  id: string
  name: string
  isDefault: boolean
  shiftType: ShiftType
  /** PR-H (spec 08 §1.5) — diensten-array uit ClientWorkSchedule. */
  diensten: ClientWorkScheduleDienstDTO[]
  /** PR-H (spec 08 §1.5) — DayCode[] bij 3/4/5-ploegen, anders null. */
  rotationPattern: string[] | null
  /** PR-H (spec 08 §1.5) — ISO date string, anders null. */
  rotationStartDate: string | null
  /** PR-H (spec 08 §1.5) — cycluslengte bij rotatie, anders null. */
  rotationCycleDays: number | null
  /**
   * FO-061 (batch-18) — per-datum-uitzonderingen op het rooster; gevoed aan de
   * dienst-resolver voor de losse-dag-bijplan-preview. Optioneel (oudere clients
   * verwachten 'm niet); leeg = geen afwijkingen.
   */
  dienstUitzonderingen?: ClientWorkScheduleUitzonderingDTO[]
  /**
   * FO-423 (batch-56) — klant-brede standaard ARBEIDSpauze in minuten; `null` =
   * geen klant-afspraak (de cascade valt door naar de systeemdefault per
   * diensttype). Optioneel zodat oudere clients ongewijzigd blijven werken.
   *
   * NB: `diensten[].breakMinutes` in dit DTO is de RESOLVEDE pauze - de server
   * heeft de cascade er al op toegepast, zodat de create-modal exact toont wat
   * er straks gepland wordt.
   */
  standaardPauzeMinuten?: number | null
  /** FO-473 - klant-default verloning; `null` = de systeemstandaard beslist. */
  standaardPauzeVerloning?: 'GESCHREVEN' | 'DOORBETAALD' | null
  /** FO-473 - klant-default factuurpauze; `null` = volgt de verloningsregel. */
  standaardPauzeFacturatieMinuten?: number | null
  /** FO-473 - ingangsdatum (ISO) van de factuurpauze-afspraken; `null` = altijd. */
  facturatiePauzeVanaf?: string | null
}

export interface WerkbonCreateContext {
  client: {
    id: string
    name: string
    street: string | null
    houseNumber: string | null
    postalCode: string | null
    city: string | null
    locations: Array<{
      id: string
      name: string
      street: string | null
      houseNumber: string | null
      postalCode: string | null
      city: string | null
    }>
    /**
     * batch-63 (FO-474) — de klant-default voor nieuwe werkbonnen. `false` = de
     * server zet een nieuwe bon op niet facturabel met reden KLANT_INSTELLING,
     * tenzij de gebruiker het facturabel-veld bewust aanraakt.
     */
    werkbonFacturabelDefault: boolean
  } | null
  /** Backward-compat default tariff (eerste isDefault, anders eerste rij). */
  clientTariff: ClientTariffDTO | null
  /** BLOK 4: alle tarieven van de klant voor de dropdown. */
  clientTariffs: ClientTariffOptionDTO[]
  /** BLOK 4: alle werkschema's van de klant voor de dropdown. */
  workSchedules: ClientWorkScheduleOptionDTO[]
  order: {
    id: string
    orderNumber: string
    subject: string
    scheduledDate: string | null
    quoteId: string | null
    clientId: string | null
  } | null
  quote: {
    quoteNumber: string
    packageType: PackageType | null
    hourlyRate: number | null
    shiftType: ShiftType | null
  } | null
  employees: Array<{
    id: string
    firstName: string
    lastName: string
    employeeNumber: string | null
  }>
  projectLeaders: Array<{
    id: string
    firstName: string
    lastName: string
    employeeNumber: string | null
  }>
  /** Per werkbon-type de API-side hardcoded uurtarief-fallback (KANTOOR = 0). */
  defaultTariffs: Record<WerkbonType, number>
  /** FO-473 - de ingestelde pauze-systeemstandaard (voor de uren-preview). */
  pauzeSysteemDefaults?: PauzeSysteemDefaultsDTO
}

export interface InvoiceLineDTO {
  id: string
  werkbonId: string | null
  werkbonNumber: string | null
  position: number
  category: InvoiceLineCategory
  description: string
  quantity: number
  unitPrice: number
  total: number
}

export interface InvoiceDTO {
  id: string
  invoiceNumber: string
  clientId: string
  entityId: string
  status: InvoiceStatus
  weekNr: string
  weekLabel: string
  type: WerkbonType
  subtotal: number
  vatAmount: number
  total: number
  vatPercentage: number
  totaalUren: number
  totaalReisKm: number
  totaalReisMinuten: number
  totaalPloegentoeslag: number
  packageType: PackageType | null
  shiftType: ShiftType | null
  lines: InvoiceLineDTO[]
  createdAt: string
}

// ─── List & overview DTOs ──────────────────────────────────────────────────
//
// Shape afkomstig uit GET /werkbonnen (afgeslankte rij voor de lijstweergave).

export interface WerkbonListEmployeeDTO {
  id: string
  firstName: string
  lastName: string
  employeeNumber: string | null
}

export interface WerkbonListDayEntryDTO {
  id: string
  date: string
  plannedStart: string
  plannedEnd: string
  hours: number
  status: DayEntryStatus
  employee: WerkbonListEmployeeDTO | null
}

/**
 * BLOK 4: kompacte child-werkbon shape die de lijst-API meeneemt bij
 * `?tree=true`. Velden zijn beperkt tot wat de tree-toggle nodig heeft.
 */
export interface WerkbonListChildDTO {
  id: string
  werkbonNumber: string
  status: WerkbonStatus
  type: WerkbonType
  weekNr: string
  startDatum: string
  eindDatum: string
  dienstType: DienstType
  ploegSoort: PloegSoort | null
  dayEntriesCount: number
}

export interface WerkbonListRowDTO {
  id: string
  werkbonNumber: string
  // Step 5A/5B — menselijk leesbare naam + vrije omschrijving. Optioneel omdat
  // oudere clients ze niet verwachten; de medewerker-detail-tab toont ze.
  displayName?: string | null
  omschrijving?: string | null
  status: WerkbonStatus
  // Werkbon-status vereenvoudiging (2026-05-27) — de lijst-serializer levert
  // deze drie mee zodat de UI de goedkeuring-/Gefactureerd-/Verloond-badges kan
  // tonen. Optioneel omdat oudere clients ze niet verwachten.
  goedkeuringStatus?: WerkbonGoedkeuringStatus | null
  isVerwerktInVerloning?: boolean
  /** Facturatie = invoiceId != null. */
  invoiceId?: string | null
  /**
   * batch-61 werkbon-gefactureerd (FO-449) - het afgeleide facturatie-spoor
   * (Nog te factureren / Gefactureerd via Finqle / handmatig / via factuur X /
   * Niet te factureren) uit de gedeelde resolver `facturatieStatusVan`.
   * Optioneel omdat oudere clients (portal-planning) 'm niet lezen.
   */
  facturatie?: WerkbonFacturatieDTO
  /** batch-65 (FO-484 §C.7) — "in bundel VB-…" op de lijstrij (null = geen lopende bundel). */
  bundel?: WerkbonBundelRefDTO | null
  // Afkeur-indicatoren (ISO-strings) voor de planning: bij een afkeur staat
  // goedkeuringStatus op null en is één van deze tijdstempels gezet.
  afgekeurdOp?: string | null
  klantWeigeringOp?: string | null
  type: WerkbonType
  /** DAGDIENST / PLOEGEN_2 / PLOEGEN_3 / PLOEGEN_5 — voor sidebar-label. */
  dienstType: DienstType
  /** Onderdeel van een recurring-serie (parentWerkbonId niet null). */
  isRecurring: boolean
  /** BLOK 4: deze werkbon is een hoofdbon waar children onder hangen. */
  isParent?: boolean
  /** BLOK 4: child-werkbonnen, alleen aanwezig bij `?tree=true` op een parent. */
  children?: WerkbonListChildDTO[]
  /** BLOK 4: aantal children, alleen aanwezig bij `?tree=true` op een parent. */
  childrenCount?: number
  weekNr: string
  startDatum: string
  eindDatum: string
  employees: WerkbonListEmployeeDTO[]
  /**
   * Directe client van de werkbon. `werkbon.clientId` is in de DB verplicht
   * (sinds migratie 20260505200016), MAAR de lijst-DTO levert `client: null`
   * wanneer de werkbon is afgeschermd (FO-008 besloten). Lees dit veld dus
   * ALTIJD null-safe; `order.client` is daarnaast null bij orderloze werkbonnen.
   */
  client: { id: string; name: string; clientNumber: string | null } | null
  /**
   * FO-008 (besloten afspraak) — `besloten` markeert een vertrouwelijke werkbon;
   * `afgeschermd` = de inhoud is verborgen voor de huidige viewer. Bij afgeschermd
   * levert de werkbonnen-route `client`/`employees`/`clientLocation` als null/leeg
   * en `displayName` = "Besloten" - consumers MOETEN `client` daarom null-safe
   * lezen en bij `afgeschermd` een neutraal "Besloten" tonen.
   */
  besloten?: boolean
  afgeschermd?: boolean
  order: {
    id: string
    orderNumber: string
    title: string
    clientLocationId: string | null
    client: { id: string; name: string; clientNumber: string | null } | null
  } | null
  dayEntries: WerkbonListDayEntryDTO[]
  ingediendeDagenCount: number
  totaalUren: number
  /**
   * Som van (uren × uurtarief) + (overuren × overuurtarief), excl. materiaal/reis.
   *
   * PR-E3 (spec financiele-afscherming §7.1) — wordt door de API gestript voor
   * users zonder `FACTUREN.canView`. Daarom optioneel.
   */
  totaalEuro?: number
  totaalReisMinuten: number
  goedgekeurdOp: string | null
  clientLocationId: string | null
  // FO-100/FO-110 (batch-21) — uitvoerende BV (entiteit) van de werkbon, zodat de
  // medewerker-/relatie-werkbonnen-tab een entiteit-kolom kan tonen en het
  // entityId-filter zichtbaar resultaat geeft. Optioneel (oudere clients lezen 'm
  // niet); `null` bij afgeschermde (besloten) werkbonnen of geen BV-koppeling.
  entiteit?: { id: string; naam: string } | null
  // Fase 3 (cursus-multidag) — bron-koppeling. De lijst-serializer spreidt
  // `resolveBron(w)` mee; optioneel omdat oudere clients ze niet verwachten.
  bronType?: 'CURSUS' | 'KALENDER' | null
  bronLabel?: string | null
}

export interface WerkbonStatsDTO {
  totaal: number
  ingediend: number
  teBeoordelenWerkbonnen: number
  ingediendeDagen: number
  gefactureerdAantal: number
  gefactureerdBedrag: number
  gemiddeldBedrag: number
}

// ─── Zod-schemas (request-validatie) ────────────────────────────────────────
//
// PR-2 (spec 07 §3.3) — UpdateWerkbonSchema is **volledig bewerkbaar** zodra
// de werkbon nog niet INGEDIEND/GOEDGEKEURD/GEFACTUREERD is. De route handler
// (`PATCH /werkbonnen/:id`) splitst body in structurele vs metadata-velden
// en blokkeert structurele wijzigingen bij status INGEDIEND/GOEDGEKEURD
// (spec §3.2). GEFACTUREERD blokkeert alles.
//
// Velden zijn allemaal `optional()` — de PATCH update alleen de meegegeven
// velden. `nullable()` is bewust waar het schema explicit `null` toestaat
// (bv. clientContactId clear-naar-loslate).

const isoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet yyyy-mm-dd zijn')
const isoDateTimeString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/, 'Datum moet yyyy-mm-dd of ISO-8601 zijn')
const cuidString = z.string().min(1)

export const WERKBON_STRUCTURELE_VELDEN = [
  'clientId',
  'projectId',
  'type',
  'startDatum',
  'eindDatum',
  'dienstType',
  'ploegSoort',
  'overuurtarief',
  'kmVergoeding',
  // Ronde 4 W6b (spec 06b §1.3) — quoteId verandert de bron-traceability van de
  // werkbon; daarom structureel (alleen wijzigbaar zolang status CONCEPT is).
  'quoteId',
] as const
export type WerkbonStructureelVeld = (typeof WERKBON_STRUCTURELE_VELDEN)[number]

export const WERKBON_METADATA_VELDEN = [
  'omschrijving',
  'referentie',
  'opmerkingen',
  'uurtarief',
  'factuurType',
  'projectLeaderId',
  // Ronde 4 W6 — Order-velden gemigreerd naar werkbon-metadata
  'clientContactId',
  'priority',
  'deadline',
  // Werkbon-status vereenvoudiging (2026-05-27) — per-werkbon override op de
  // klant-akkoord-stap. null = volg de klant-modus (Client.klantGoedkeuring === 'WERKBON').
  'klantAkkoordVereistOverride',
  // A7 / B6 — facturabel-vlag + vervangingsbehoefte zijn metadata: bewerkbaar
  // zolang de werkbon niet gefactureerd/verloond is (net als factuurType).
  'facturabel',
  // batch-63 (FO-474) — de reden hoort bij de vlag.
  'facturabelReden',
  'vervangingsStatus',
] as const
export type WerkbonMetadataVeld = (typeof WERKBON_METADATA_VELDEN)[number]

export const updateWerkbonSchema = z
  .object({
    // ── Structurele velden (geblokkeerd bij INGEDIEND/GOEDGEKEURD/GEFACTUREERD)
    clientId: cuidString.optional(),
    projectId: cuidString.nullable().optional(),
    type: z.enum(['SERVICE', 'ONDERHOUD', 'PROJECT']).optional(),
    startDatum: isoDateString.optional(),
    eindDatum: isoDateString.optional(),
    dienstType: z
      .enum(['DAGDIENST', 'PLOEGEN_2', 'PLOEGEN_3', 'PLOEGEN_4', 'PLOEGEN_5'])
      .optional(),
    ploegSoort: z.enum(['DAG', 'VROEG', 'LAAT', 'NACHT']).nullable().optional(),
    overuurtarief: z.number().min(0).nullable().optional(),
    // Ronde 4 W6b (spec 06b §1.3) — structureel veld: alleen wijzigbaar zolang
    // status CONCEPT is (handler blokkeert structurele wijziging vanaf INGEDIEND).
    quoteId: cuidString.nullable().optional(),
    // Ronde 4 W6 — Order-velden (priority/contactpersoon/deadline) gemigreerd
    // naar de werkbon zelf en als metadata bewerkbaar tot status GEFACTUREERD.
    clientContactId: cuidString.nullable().optional(),
    priority: z.enum(['LAAG', 'NORMAAL', 'HOOG', 'KRITIEK']).optional(),
    deadline: isoDateTimeString.nullable().optional(),
    /**
     * Klant-overstijgende km-vergoeding per km. Schemaveld op werkbon.tariff
     * (BLOK 4); hier alleen meegenomen voor de PATCH UI — niet door deze
     * route geüpdatet (zou tariff-niveau zijn). Wordt momenteel als opmerking
     * bewaard in audit-trail.
     */
    kmVergoeding: z.number().min(0).nullable().optional(),

    // Optioneel — bij wijziging van dienstType/ploegSoort kan de UI (na
    // bevestiging) de nieuwe placeholder-dagtijden meesturen om te herleiden.
    herleidDagtijden: z
      .object({
        plannedStart: z.string().regex(/^\d{2}:\d{2}$/),
        plannedEnd: z.string().regex(/^\d{2}:\d{2}$/),
      })
      .optional(),

    // ── Metadata (altijd bewerkbaar behalve GEFACTUREERD)
    omschrijving: z.string().min(1).nullable().optional(),
    referentie: z.string().nullable().optional(),
    opmerkingen: z.string().nullable().optional(),
    uurtarief: z.number().min(0).optional(),
    // A3 — alleen nog NACALCULATIE; vaste prijs is verwijderd.
    factuurType: z.enum(['NACALCULATIE']).optional(),
    // A7 — facturabel ja/nee (overrulebaar). B6 — vervangingsbehoefte op de
    // werkbon. Beide metadata: bewerkbaar tot de werkbon gefactureerd/verloond is.
    facturabel: z.boolean().optional(),
    // batch-63 (FO-474) — `facturabel: false` alleen mét reden (en een reden
    // alleen bij `facturabel: false`); zie `controleerFacturabelKeuze` hieronder.
    // Nullable: de read-DTO geeft `null` terug bij een facturabele bon.
    facturabelReden: facturabelRedenSchema.nullable().optional(),
    vervangingsStatus: z.enum(['GEEN', 'GEWENST', 'VERPLICHT']).optional(),
    projectLeaderId: cuidString.nullable().optional(),
    // Werkbon-status vereenvoudiging (2026-05-27) — per-werkbon override op de
    // klant-akkoord-stap. null = volg de klant-modus (Client.klantGoedkeuring ===
    // 'WERKBON'); true/false dwingt het respectievelijk af of slaat het over.
    klantAkkoordVereistOverride: z.boolean().nullable().optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    // Alleen wanneer het facturabel-veld bewust is aangeraakt: een PATCH zonder
    // `facturabel` laat vlag én reden ongemoeid.
    if (body.facturabel === undefined && body.facturabelReden === undefined) return
    controleerFacturabelKeuze(body, ctx)
  })

export type UpdateWerkbonInput = z.infer<typeof updateWerkbonSchema>

/**
 * Bevat de body één of meer structurele velden? Helper voor route-handler om
 * status-restricties uit spec §3.2 toe te passen.
 */
export function hasStructureleVelden(body: UpdateWerkbonInput): boolean {
  return WERKBON_STRUCTURELE_VELDEN.some(
    (veld) => (body as Record<string, unknown>)[veld] !== undefined,
  )
}

/**
 * Lijst van structurele velden die in de body voorkomen. Voor foutmeldingen.
 */
export function changedStructureleVelden(body: UpdateWerkbonInput): WerkbonStructureelVeld[] {
  return WERKBON_STRUCTURELE_VELDEN.filter(
    (veld) => (body as Record<string, unknown>)[veld] !== undefined,
  )
}

// ─── Approval-endpoint schemas ─────────────────────────────────────────────

export const werkbonAfkeurSchema = z.object({
  redenen: z
    .array(z.enum(DAY_REJECT_REASONS as unknown as [DayRejectReason, ...DayRejectReason[]]))
    .min(1),
  toelichting: z.string().max(2000).nullable().optional(),
})

export const werkbonBulkGoedkeurenSchema = z.object({
  werkbonIds: z.array(cuidString).min(1).max(200),
})

// Werkbon-status vereenvoudiging (2026-05-27) — interne afkeuring (bulk). De
// opmerking (`toelichting`) is verplicht (de monteur moet weten wat te
// corrigeren); `redenen` is optioneel — afkeur kan op vrije tekst alleen.
export const werkbonBulkAfkeurenSchema = z.object({
  werkbonIds: z.array(cuidString).min(1).max(200),
  toelichting: z.string().trim().min(1, 'Opmerking is verplicht bij afkeuring.').max(2000),
  redenen: z
    .array(z.enum(DAY_REJECT_REASONS as unknown as [DayRejectReason, ...DayRejectReason[]]))
    .optional(),
})

export const werkbonDagHeropenSchema = z.object({
  dayEntryId: cuidString,
  reden: z.string().min(1).max(500),
})

const time15Pattern = z
  .string()
  .regex(/^\d{2}:(00|15|30|45)$/, 'Tijd moet op 00/15/30/45 minuten staan')

export const werkbonDagHeropenBackofficeSchema = z.object({
  dayEntryId: cuidString,
  reden: z.string().min(1).max(500),
  actualStart: time15Pattern.optional(),
  actualEnd: time15Pattern.optional(),
  pauzeMinutes: z.number().int().min(0).max(240).optional(),
})

export interface BulkApprovalResult {
  succeeded: string[]
  failed: Array<{ id: string; reason: string }>
  /**
   * batch-69 (FO-511, review 2) - goedgekeurd, maar de klantstap wacht op de
   * pauzeregel van de klant (verzend-gate aan). Ze gaan vanzelf naar de klant.
   */
  klantstapVastgehouden?: string[]
}

// ─── Goedkeuringen-inbox werkbon-detail DTO (spec 07 §1.8) ────────────────
//
// De goedkeuringen-inbox bevat sinds PR-2 (spec 07 §1.8) een werkbon-niveau
// item-detail (i.p.v. dagen-niveau). Aparte DTO want we willen ook periode-,
// dag-aantal- en totaal-uren-velden meesturen voor de inbox-tabel.

export interface GoedkeuringenInboxWerkbonDayEntryDTO {
  date: string
  uren: number
  reisMinuten: number
  reisKm: number
}

export interface GoedkeuringenInboxWerkbonDetailDTO {
  id: string
  nummer: string
  klant: string
  omschrijving: string
  type: WerkbonType
  ingediendOp: string | null
  ingediendDoor: string | null
  aantalDagen: number
  totaalUren: number
  periodeVan: string
  periodeTot: string
  dayEntries: GoedkeuringenInboxWerkbonDayEntryDTO[]
}

// ─── Planning klanten-dropdown DTO (spec 07 §4.5) ────────────────────────

export interface PlanningKlantDTO {
  id: string
  naam: string
  aantalWerkbonnen: number
}

// ─── Spec A §SS14 — Werkbon-modificatie + reistijd-override ────────────────────

import { z as zModi } from 'zod'

/** Scope-resolutie voor bulk-mutaties op werkbon-dag-entries (Spec A §SS11.3). */
export const ModificatieScopeSchema = zModi.enum([
  'ALLEEN_DEZE_DAG',
  'HELE_WEEK',
  'HELE_CYCLUS',
  'GEHELE_REEKS',
  'HANDMATIG',
])
export type ModificatieScope = zModi.infer<typeof ModificatieScopeSchema>

/** Reden voor handmatige reistijd-override (Spec A §SS9). */
export const TravelOverrideReasonSchema = zModi.enum([
  'CARPOOL',
  'VANUIT_ANDERE_KLANT',
  'HOTEL',
  'TIJDELIJKE_LOCATIE',
  'KLANTAFSPRAAK',
  'OVERIG',
])
export type TravelOverrideReason = zModi.infer<typeof TravelOverrideReasonSchema>

/** Modificatie-payload — combineert scope + wijziging (Spec A §SS11). */
export const WerkbonModificatieSchema = zModi.object({
  scope: ModificatieScopeSchema,
  dayEntryIds: zModi.array(zModi.string()).optional(),
  wijziging: zModi.object({
    plannedStart: zModi.string().optional(),
    plannedEnd: zModi.string().optional(),
    pauzeMinutes: zModi.number().int().min(0).optional(),
    employeeId: zModi.string().optional(),
    clientLocationId: zModi.string().optional(),
  }),
})
export type WerkbonModificatie = zModi.infer<typeof WerkbonModificatieSchema>

/** Lijst-DTO voor backoffice werkbonnen-overzicht — bevat directe FK's (Spec A §SS3). */
export interface WerkbonListItemDTO {
  id: string
  werkbonNumber: string
  displayName: string | null
  status: WerkbonStatus
  type: WerkbonType
  dienstType: string
  ploegSoort: string | null
  client: { id: string; name: string }
  clientLocation: { id: string; name: string } | null
  entity: { id: string; name: string } | null
  planningGroupId: string | null
  startDatum: string
  eindDatum: string
  uurtarief: number
}

/** Planning-bord dag-entry-DTO — werkbon-snapshot ingebed (Spec A §SS7.3). */
export interface PlanningDayEntryDTO {
  id: string
  date: string
  plannedStart: string
  plannedEnd: string
  hours: number
  status: string
  employeeId: string | null
  werkbon: {
    id: string
    werkbonNumber: string
    displayName: string | null
    status: string
    type: string
    dienstType: string
    ploegSoort: string | null
    client: { id: string; name: string }
    clientLocation: { id: string; name: string } | null
    entity: { id: string; name: string } | null
    planningGroupId: string | null
    isConsignatie: boolean
    consignatieType: string | null
  }
}

/**
 * Audit-actie keys voor Spec A §SS14.1.
 *
 * Bestaande WerkbonAuditAction in `apps/api/src/lib/werkbon-audit.ts` wordt
 * uitgebreid met deze nieuwe acties — geen Prisma-enum, blijft een string-
 * union zodat oudere routes geen schema-migratie nodig hebben.
 */
export type WerkbonAuditActionExtension =
  | 'REISTIJD_OVERRIDE'
  | 'MEDEWERKER_VERVANGEN'
  | 'EXTRA_INZET'
  | 'SCOPE_WIJZIGING'
  | 'DISPLAY_NAME_BIJGEWERKT'

// ─── Bulk-planbord-acties (planbord scope-popup) ────────────────────────────
// Drie scope-acties op het planbord (verwijderen / tijden wijzigen /
// verplaatsen) met een Google-Calendar-achtige scope-keuze. De wire-waarden
// zijn identiek aan het bestaande PATCH /werkbonnen/:id/planning endpoint zodat
// de hele keten (scope-popup → server-action → API) één scope-vocabulaire deelt.
// `this-weekday-future` = de scope "deze weekdag, alle toekomstige weken":
// alle dagen in de reeks-boom met dezelfde ISO-weekdag (Europe/Amsterdam) als
// de aangeklikte dag, vanaf die dag (inclusief) tot een optionele `eindDatum`.
// Vereist een `dayEntryId` (anchor) net als `only-this-day`.
export const bulkScopeEnum = z.enum([
  'only-this-day',
  'only-this-werkbon',
  'this-and-future',
  'this-weekday-future',
])
export type BulkScope = z.infer<typeof bulkScopeEnum>

/** ISO-datum 'yyyy-mm-dd' voor de optionele `eindDatum` bij `this-weekday-future`. */
const bulkEindDatumSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'eindDatum moet yyyy-mm-dd zijn')

/** True als `scope` een `dayEntryId`-anchor vereist (only-this-day + weekdag-toekomst). */
export function scopeRequiresDayEntry(scope: BulkScope): boolean {
  return scope === 'only-this-day' || scope === 'this-weekday-future'
}

/**
 * Body voor POST /werkbonnen/:id/bulk-unplan — soft-delete van dayEntries
 * binnen de gekozen scope. `z.string()` (geen `.cuid()`) omdat productie nog
 * legacy UUID-format IDs bevat.
 */
export const bulkUnplanInputSchema = z
  .object({
    scope: bulkScopeEnum,
    /** Verplicht bij scope `only-this-day` en `this-weekday-future` (anchor). */
    dayEntryId: z.string().optional(),
    /** Optioneel: beperk tot dayEntries van déze monteur (shared werkbon). */
    employeeId: z.string().optional(),
    /** Optionele einddatum (yyyy-mm-dd) bij scope `this-weekday-future`; leeg = onbegrensd vooruit. */
    eindDatum: bulkEindDatumSchema.optional(),
  })
  .refine((d) => !scopeRequiresDayEntry(d.scope) || !!d.dayEntryId, {
    message: 'dayEntryId is verplicht bij deze scope',
    path: ['dayEntryId'],
  })
export type BulkUnplanInput = z.infer<typeof bulkUnplanInputSchema>

/**
 * Body voor POST /werkbonnen/:id/bulk-reassign — verplaats dayEntries binnen de
 * gekozen scope naar `targetEmployeeId`.
 */
export const bulkReassignInputSchema = z
  .object({
    scope: bulkScopeEnum,
    /** Verplicht bij scope `only-this-day` en `this-weekday-future` (anchor). */
    dayEntryId: z.string().optional(),
    /** Doel-monteur (employeeId) waar de dagen naartoe gaan. */
    targetEmployeeId: z.string().min(1),
    /** Optioneel: verplaats alleen dagen van déze bron-monteur (shared werkbon). */
    sourceEmployeeId: z.string().optional(),
    /** Optionele einddatum (yyyy-mm-dd) bij scope `this-weekday-future`; leeg = onbegrensd vooruit. */
    eindDatum: bulkEindDatumSchema.optional(),
    // Batch-6 cert §5b — certificaat-screening: waarschuwingen accepteren /
    // blokkade overrulen met reden (PLANNING.canApprove, audit + HR-melding).
    certAkkoord: z.boolean().optional(),
    certOverrideReden: z.string().max(500).optional(),
  })
  .refine((d) => !scopeRequiresDayEntry(d.scope) || !!d.dayEntryId, {
    message: 'dayEntryId is verplicht bij deze scope',
    path: ['dayEntryId'],
  })
export type BulkReassignInput = z.infer<typeof bulkReassignInputSchema>

/** Eén overgeslagen werkbon/dag bij een bulk-actie (status-lock). */
export interface BulkSkippedEntry {
  id: string
  werkbonNumber: string
  reason: string
}

/** Response-data van POST /werkbonnen/:id/bulk-unplan. */
export interface BulkUnplanResult {
  deleted: number
  skipped: BulkSkippedEntry[]
}

/**
 * Eén werkbon waarvan de omschrijving/referentie automatisch is hernoemd nadat
 * alle (resterende) dagen naar een andere medewerker gingen (werkbon-naamgeving
 * bij omplannen). `omschrijving`/`referentie` bevatten de NIEUWE waarde;
 * een ontbrekend veld is niet gewijzigd.
 */
export interface WerkbonHernoemdDTO {
  werkbonId: string
  werkbonNumber: string
  omschrijving?: string
  referentie?: string
}

/** Response-data van POST /werkbonnen/:id/bulk-reassign. */
export interface BulkReassignResult {
  reassigned: number
  skipped: BulkSkippedEntry[]
  /** Werkbonnen die door de medewerker-wissel automatisch zijn hernoemd. */
  hernoemd: WerkbonHernoemdDTO[]
}

// ─── Werkbon vanuit kalender — generator + conflict-detectie (Spec NN §3.2.3) ─
//
// Genereert werkbonnen vanuit een kalender-event of cursus-sessie per attendee.
// Conflict-detectie meldt (blokkeert niet): default krijgt een attendee mét
// conflict géén werkbon; `overrideConflictAttendeeIds` forceert die wél
// (PO-besluit 7 — melden i.p.v. blokkeren, per-attendee override). De target-
// entiteit valt bij ontbreken terug op de holding-BV (PO-besluit 4 / E1).

export const WERKBON_CONFLICT_TYPE = [
  'VERLOF',
  'ZIEK',
  'FEESTDAG',
  'ANDERE_WERKBON',
  'PERSOONLIJK_EVENT',
  // Batch-48 verjaardagen — de medewerker is die dag jarig en heeft "vrij op
  // verjaardag" gekozen. Zelfde niet-blokkerende semantiek als VERLOF/ZIEK: de
  // planner ziet het, mag doorzetten, en dat doorzetten wordt vastgelegd.
  'VERJAARDAG_VRIJ',
  'GEEN_LOCATIE_GEOCODED',
] as const
export type WerkbonConflictType = (typeof WERKBON_CONFLICT_TYPE)[number]

export const WERKBON_CONFLICT_LABELS: Record<WerkbonConflictType, string> = {
  VERLOF: 'Verlof',
  ZIEK: 'Ziek',
  FEESTDAG: 'Feestdag',
  ANDERE_WERKBON: 'Andere werkbon',
  PERSOONLIJK_EVENT: 'Persoonlijke afspraak',
  VERJAARDAG_VRIJ: 'Jarig (vrij gekozen)',
  GEEN_LOCATIE_GEOCODED: 'Locatie niet gegeocodeerd',
}

export interface WerkbonConflictDTO {
  type: WerkbonConflictType
  omschrijving: string
  datum?: string
  refId?: string
}

/**
 * Input voor het genereren van werkbonnen vanuit een kalender-event/sessie.
 * Semantiek (Spec NN §0 #7): default = geen werkbon voor attendees mét conflict;
 * `overrideConflictAttendeeIds` forceert die alsnog. `dryRun` levert alleen de
 * preview zonder aan te maken.
 *
 * `geenReisuren` (default false): zet de `geenReisuren`-vlag op álle gegenereerde
 * day-entries zodat de reisuren-engine ze overslaat. Voor werk zonder dienstreis
 * (de reistijd naar het event/de cursus wordt dan niet berekend). Afwezig/false
 * = ongewijzigd gedrag (reistijd wordt berekend).
 */
export const genereerWerkbonnenInputSchema = z.object({
  attendeeIds: z.array(idParam('attendeeId')).optional(),
  dryRun: z.boolean().optional(),
  overrideConflictAttendeeIds: z.array(idParam('attendeeId')).optional(),
  geenReisuren: z.boolean().optional(),
})
export type GenereerWerkbonnenInput = z.infer<typeof genereerWerkbonnenInputSchema>

export interface GenereerWerkbonnenAttendeePreviewDTO {
  /**
   * Id waarop `attendeeIds`/`overrideConflictAttendeeIds` filteren. Bij een
   * sessie-bron is dit het `OpleidingDeelname.id`, bij een kalender-event de
   * `KalenderEventAttendee.id`. De UI gebruikt dit om de override-toggle te
   * koppelen aan de juiste attendee. Gedeeld met de kalender-PR.
   */
  attendeeId: string
  employeeId: string
  employeeNaam: string
  targetEntityId: string | null
  targetEntityNaam: string | null
  fallbackHoldingBv: boolean
  locatieLabel: string | null
  locatieGeocoded: boolean
  reisMinutenSchatting: number | null
  conflicts: WerkbonConflictDTO[]
  bestaatAl: boolean
  willCreate: boolean
}

export interface GenereerWerkbonnenPreviewDTO {
  attendees: GenereerWerkbonnenAttendeePreviewDTO[]
  warnings: string[]
}

export interface GenereerWerkbonnenResultDTO {
  created: Array<{ employeeId: string; werkbonId: string; werkbonNumber: string }>
  skipped: Array<{ employeeId: string; reden: string }>
  conflicts: Array<{ employeeId: string; conflicts: WerkbonConflictDTO[] }>
  warnings: string[]
}

// ─── batch-56 werkbon-create (FO-423) — pauze-werklijst ──────────────────────

/**
 * Eén open pauze-vraag op een klant-werkschema.
 *
 * Twee soorten, bewust in één lijst zodat een beheerder in één scherm ziet wat
 * er nog te beslissen valt:
 *   - `dienstenZonderPauze` gevuld: de pauze is nooit expliciet vastgelegd (vóór
 *     FO-423 werd dat stilzwijgend 0). Of die pauze doorbetaald is of niet, is
 *     per klant een contractvraag - vandaar een werklijst en geen bulk-datafix.
 *   - `dubbeltelRisico`: het schema trekt de pauze zowel van de ARBEIDSuren als
 *     van de FACTURABELE uren af. Dat is een configuratiefout (dezelfde pauze
 *     gaat twee keer van de factuur).
 */
export interface PauzeWerklijstRijDTO {
  workScheduleId: string
  workScheduleNaam: string
  clientId: string
  clientNaam: string
  shiftType: string
  isDefault: boolean
  /**
   * Diensten waarvoor de pauze-keuze nog open staat: helemaal niet ingevuld, of
   * een 0-pauze die nooit expliciet is bevestigd (vóór FO-423 werd een
   * niet-ingevulde pauze opgeslagen als een gewone 0).
   */
  dienstenZonderPauze: string[]
  /** Klant-brede standaardpauze; `null` = geen afspraak. */
  standaardPauzeMinuten: number | null
  /** Facturatie-pauze van dit schema (los begrip, zie `facturatie-pauze.ts`). */
  facturatiePauzeMinuten: number
  /**
   * FO-473 - is die oude waarde al omgezet naar factuurpauze-afspraken? Dan
   * is hij alleen nog een vangnet en geen dubbeltel-risico.
   */
  oudeFacturatiePauzeOmgezet?: boolean
  /** Is de pauze ooit bewust vastgelegd onder de verplichte-keuze-regel? */
  pauzeBevestigd: boolean
  dubbeltelRisico: boolean
}

export interface PauzeWerklijstDTO {
  rijen: PauzeWerklijstRijDTO[]
  /** Alle actieve klant-werkschema's (noemer, zodat de lijst context heeft). */
  totaalSchemas: number
  aantalOntbrekend: number
  aantalDubbeltel: number
}

/** Instelbare systeemdefaults van de pauze-cascade (laatste trede). */
export interface PauzeSysteemDefaultsDTO {
  dagdienstMinuten: number
  ploegenMinuten: number
  /** FO-473 - dag geschreven / ploeg en nacht doorbetaald (instelbaar). */
  dagdienstVerloning: 'GESCHREVEN' | 'DOORBETAALD'
  ploegenVerloning: 'GESCHREVEN' | 'DOORBETAALD'
}
